/**
 * Whether the signed-in user has already seen a one-time moment (the claim view
 * tour, the first-claim reveal), and a way to record it. The contract is in
 * shared/firstClaim.ts; the state is stored per user on the server.
 */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FIRST_RUN_API, type FirstRunKey, type FirstRunState } from '@shared/firstClaim';
import { apiRequest } from '@/lib/query-client';

export const FIRST_RUN_QUERY_KEY = [FIRST_RUN_API] as const;

export interface FirstRun {
  /** True until the saved state has loaded. Treat as "not known yet": do not show the moment. */
  isLoading: boolean;
  /** True when the state failed to load. The moment stays hidden rather than risk showing it twice. */
  isError: boolean;
  completed: boolean;
  /** Records the moment as seen. Safe to call more than once. */
  complete: () => void;
}

export function useFirstRun(key: FirstRunKey, options: { enabled?: boolean } = {}): FirstRun {
  const queryClient = useQueryClient();

  const state = useQuery<FirstRunState>({
    queryKey: FIRST_RUN_QUERY_KEY,
    queryFn: async () => (await apiRequest('GET', FIRST_RUN_API)).json(),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });

  const mutation = useMutation<FirstRunState, Error>({
    mutationFn: async () => (await apiRequest('POST', `${FIRST_RUN_API}/${key}/complete`, {})).json(),
    onSuccess: (data) => queryClient.setQueryData(FIRST_RUN_QUERY_KEY, data),
  });

  // Hide immediately on dismiss, before the save returns, so a slow network never leaves the moment on screen.
  const locallyDone = useRef(false);
  const inFlight = useRef(false);

  return {
    isLoading: state.isLoading,
    isError: state.isError,
    completed: locallyDone.current || Boolean(state.data?.completed[key]),
    complete: () => {
      locallyDone.current = true;
      queryClient.setQueryData<FirstRunState>(FIRST_RUN_QUERY_KEY, (old) => ({
        completed: { ...(old?.completed ?? {}), [key]: old?.completed[key] ?? new Date().toISOString() },
      }));
      if (inFlight.current) return;
      inFlight.current = true;
      mutation.mutate(undefined, { onSettled: () => { inFlight.current = false; } });
    },
  };
}
